import type { LintDiagnostic } from '../../types/lint.js';
import type { CharterFrontmatter } from '../../types/charter.js';

/**
 * ENG-4465 / ENG-4901: snapshot of one peer agent on the same team, used by
 * `runMultiAgentRules` to validate CHARTER `multi_agent.telegram_peers`
 * entries against the team roster.
 *
 * Callers are responsible for assembling this list before linting (typically
 * by reading the team's agents + their TelegramChannelConfig from the API).
 * `runMultiAgentRules` no-ops when the charter itself has no
 * `multi_agent.telegram_peers` entries; team-context callers should omit
 * `LintContext.teamPeers` entirely (rather than passing `[]`) when running
 * outside a team context, otherwise UNKNOWN_PEER will fire on every entry.
 */
export interface TeamPeerInfo {
  agent_id: string;
  code_name: string;
  /** Numeric Telegram bot id (`from.id`) — null when the agent has no managed Telegram bot. */
  telegram_bot_id: number | null;
  /** Telegram peer-collaboration mode for this agent, or null when no telegram channel config exists. */
  telegram_peer_agent_mode: 'off' | 'listen' | 'respond' | null;
  /**
   * ENG-4970 / ENG-4972: Slack bot user_id (the `U…` identifier) — null when
   * the agent has no managed Slack bot. Optional to preserve compatibility
   * with callers that haven't wired Slack yet (Telegram-only test suites,
   * single-channel lint flows).
   */
  slack_bot_user_id?: string | null;
  /**
   * Slack peer-collaboration mode for this agent. Optional for the same
   * reason as `slack_bot_user_id`.
   */
  slack_peer_agent_mode?: 'off' | 'listen' | 'respond' | null;
}

/**
 * ENG-4938 / ENG-4929 §5.1: minimum snapshot of a cross_team_peer_grants row
 * needed by `runMultiAgentRules` to validate a charter peer's
 * `cross_team_grant_id`. Callers (typically the API or webapp) load this
 * from the grants table for grants where granted_to_team_id matches the
 * linting agent's team — i.e. inbound grants pointing at this agent.
 *
 * `bot_id` is denormalised from agents/telegram_channel_configs so the rule
 * can verify the grant's granted_agent_id actually points at the bot the
 * charter is naming. Without it, a charter could declare bot_id=X but
 * cite a grant_id authorising bot_id=Y — and the rule would miss it.
 */
export interface CrossTeamGrantSnapshot {
  grant_id: string;
  granted_agent_id: string;
  granted_to_team_id: string;
  granted_to_agent_id: string | null;
  capability_scope: 'full' | 'grandfathered';
  revoked_at: string | null;
  expires_at: string | null;
  /** Telegram bot_id of granted_agent_id, denormalised for charter cross-check. */
  granted_agent_bot_id: number | null;
  /**
   * ENG-4970 / ENG-4972: Slack `U…` user_id of granted_agent_id, denormalised
   * the same way `granted_agent_bot_id` is. Lets the slack_peers branch
   * verify the grant authorises the bot_user_id the charter declares.
   * Optional so existing callers (Telegram-only) keep working unchanged.
   */
  granted_agent_slack_user_id?: string | null;
}

export interface MultiAgentRuleContext {
  /** Inbound cross-team grants — grants where granted_to_team_id matches the linting team. */
  crossTeamGrants?: CrossTeamGrantSnapshot[];
  /** ISO timestamp to compare expiry against; defaults to now(). Injectable for deterministic tests. */
  now?: () => Date;
}

export function runMultiAgentRules(
  charter: CharterFrontmatter,
  teamPeers: TeamPeerInfo[],
  ctx: MultiAgentRuleContext = {},
): LintDiagnostic[] {
  const diagnostics: LintDiagnostic[] = [];
  const telegramPeers = charter.multi_agent?.telegram_peers;
  const slackPeers = charter.multi_agent?.slack_peers;

  if (
    (!telegramPeers || telegramPeers.length === 0) &&
    (!slackPeers || slackPeers.length === 0)
  ) {
    return diagnostics;
  }

  const now = (ctx.now ?? (() => new Date()))();
  // CodeRabbit (post-merge of #865): preserve the three-state distinction
  // between "snapshot not loaded" (undefined), "loaded and empty" ([]),
  // and "non-empty". Defaulting to [] used to turn every cross-team peer
  // into a GRANT_INVALID for callers that hadn't wired the grants
  // fetcher yet — false positive that masks real lint issues.
  const grants = ctx.crossTeamGrants;

  // Telegram loop unchanged from ENG-4938.
  if (telegramPeers && telegramPeers.length > 0) {
    runTelegramPeerRules(diagnostics, charter, telegramPeers, teamPeers, grants, now);
  }

  // ENG-4970 / ENG-4972: parallel loop for slack_peers. Same rules
  // (SELF_PEER / GRANT_INVALID / GRANT_GRANDFATHERED / UNKNOWN_PEER /
  // CODE_NAME_MISMATCH / PEER_OPTED_OUT) but keyed on Slack bot_user_id.
  if (slackPeers && slackPeers.length > 0) {
    runSlackPeerRules(diagnostics, charter, slackPeers, teamPeers, grants, now);
  }

  return diagnostics;
}

function runTelegramPeerRules(
  diagnostics: LintDiagnostic[],
  charter: CharterFrontmatter,
  peers: NonNullable<NonNullable<CharterFrontmatter['multi_agent']>['telegram_peers']>,
  teamPeers: TeamPeerInfo[],
  grants: CrossTeamGrantSnapshot[] | undefined,
  now: Date,
): void {
  for (let i = 0; i < peers.length; i++) {
    const peer = peers[i]!;
    const path = `multi_agent.telegram_peers[${i}]`;
    const match = teamPeers.find((p) => p.telegram_bot_id === peer.bot_id);

    // Self-peer detection covers both surfaces: the declared code_name matching
    // the charter's own code_name, AND the bot_id resolving to the charter's
    // own agent_id (which catches a charter pointing bot_id at itself but
    // labelling it under a different code_name — would otherwise downgrade to
    // a CODE_NAME_MISMATCH warning and slip past).
    if (peer.code_name === charter.code_name || match?.agent_id === charter.agent_id) {
      diagnostics.push({
        file: 'CHARTER.md',
        code: 'CHARTER.MULTI_AGENT.SELF_PEER',
        path,
        severity: 'error',
        message: `Agent "${charter.code_name}" cannot list itself as a peer`,
      });
      continue;
    }

    // Cross-team peers — see §5.1. `cross_team_grant_id` swaps the
    // same-team roster check for a grants-table check. The grant must:
    //  - exist in the supplied snapshot (inbound grants for this team)
    //  - not be revoked or expired
    //  - point at an agent whose Telegram bot_id matches peer.bot_id
    //  - if granted_to_agent_id is set, match the charter's agent_id
    // GRANT_GRANDFATHERED is a warning, not an error — Slack backfill
    // (ENG-4936) issues these for cross-org pairs already chatting in
    // the wild; admins are expected to confirm or revoke them.
    if (peer.cross_team_grant_id) {
      // Snapshot not provided — caller hasn't wired the grants fetcher
      // (CLI lint, generator self-checks, etc.). Skip grant validation
      // rather than mass-firing GRANT_INVALID. The lint is still useful
      // for the rest of the multi-agent rules; UI / API callers that
      // care about grant freshness will pass a (possibly empty) array.
      if (grants === undefined) {
        continue;
      }
      const grant = grants.find((g) => g.grant_id === peer.cross_team_grant_id);
      if (!grant) {
        diagnostics.push({
          file: 'CHARTER.md',
          code: 'CHARTER.MULTI_AGENT.GRANT_INVALID',
          path,
          severity: 'error',
          message: `cross_team_grant_id "${peer.cross_team_grant_id}" is not a known grant authorising this team to address peer "${peer.code_name}"`,
        });
        continue;
      }
      if (grant.revoked_at) {
        diagnostics.push({
          file: 'CHARTER.md',
          code: 'CHARTER.MULTI_AGENT.GRANT_INVALID',
          path,
          severity: 'error',
          message: `cross_team_grant_id "${peer.cross_team_grant_id}" was revoked at ${grant.revoked_at}`,
        });
        continue;
      }
      if (grant.expires_at && new Date(grant.expires_at) <= now) {
        diagnostics.push({
          file: 'CHARTER.md',
          code: 'CHARTER.MULTI_AGENT.GRANT_INVALID',
          path,
          severity: 'error',
          message: `cross_team_grant_id "${peer.cross_team_grant_id}" expired at ${grant.expires_at}`,
        });
        continue;
      }
      if (grant.granted_agent_bot_id !== peer.bot_id) {
        diagnostics.push({
          file: 'CHARTER.md',
          code: 'CHARTER.MULTI_AGENT.GRANT_INVALID',
          path,
          severity: 'error',
          message: `cross_team_grant_id "${peer.cross_team_grant_id}" authorises bot_id ${grant.granted_agent_bot_id ?? 'null'}, but charter peer declares bot_id ${peer.bot_id}`,
        });
        continue;
      }
      if (grant.granted_to_agent_id && grant.granted_to_agent_id !== charter.agent_id) {
        diagnostics.push({
          file: 'CHARTER.md',
          code: 'CHARTER.MULTI_AGENT.GRANT_INVALID',
          path,
          severity: 'error',
          message: `cross_team_grant_id "${peer.cross_team_grant_id}" is scoped to agent_id ${grant.granted_to_agent_id}, but this charter is for agent_id ${charter.agent_id}`,
        });
        continue;
      }
      if (grant.capability_scope === 'grandfathered') {
        diagnostics.push({
          file: 'CHARTER.md',
          code: 'CHARTER.MULTI_AGENT.GRANT_GRANDFATHERED',
          path,
          severity: 'warning',
          message: `cross_team_grant_id "${peer.cross_team_grant_id}" is a Slack-backfill grandfathered grant for peer "${peer.code_name}". Confirm or revoke from team settings.`,
        });
      }
      // Cross-team grant validated — skip same-team roster checks below,
      // which would otherwise fire UNKNOWN_PEER / PEER_OPTED_OUT against
      // the foreign agent we have no roster info for.
      continue;
    }

    if (!match) {
      diagnostics.push({
        file: 'CHARTER.md',
        code: 'CHARTER.MULTI_AGENT.UNKNOWN_PEER',
        path,
        severity: 'error',
        message: `No agent on this team has a Telegram bot with bot_id ${peer.bot_id} (declared peer "${peer.code_name}")`,
      });
      continue;
    }

    if (match.code_name !== peer.code_name) {
      diagnostics.push({
        file: 'CHARTER.md',
        code: 'CHARTER.MULTI_AGENT.CODE_NAME_MISMATCH',
        path,
        severity: 'warning',
        message: `bot_id ${peer.bot_id} belongs to agent "${match.code_name}", but is listed under code_name "${peer.code_name}"`,
      });
    }

    if (match.telegram_peer_agent_mode === null || match.telegram_peer_agent_mode === 'off') {
      diagnostics.push({
        file: 'CHARTER.md',
        code: 'CHARTER.MULTI_AGENT.PEER_OPTED_OUT',
        path,
        severity: 'error',
        message: `Peer "${match.code_name}" has peer_agent_mode "${match.telegram_peer_agent_mode ?? 'unset'}"; set it to 'listen' or 'respond' on that agent's Telegram channel config`,
      });
    }
  }
}

/**
 * ENG-4970 / ENG-4972: Slack parallel of `runTelegramPeerRules`. Same
 * structure, keyed on `bot_user_id` and `granted_agent_slack_user_id`
 * instead of the Telegram integer pair. Same six lint codes; the path
 * prefix (`multi_agent.slack_peers[i]`) keeps diagnostics distinguishable.
 */
function runSlackPeerRules(
  diagnostics: LintDiagnostic[],
  charter: CharterFrontmatter,
  peers: NonNullable<NonNullable<CharterFrontmatter['multi_agent']>['slack_peers']>,
  teamPeers: TeamPeerInfo[],
  grants: CrossTeamGrantSnapshot[] | undefined,
  now: Date,
): void {
  for (let i = 0; i < peers.length; i++) {
    const peer = peers[i]!;
    const path = `multi_agent.slack_peers[${i}]`;
    const match = teamPeers.find((p) => p.slack_bot_user_id === peer.bot_user_id);

    if (peer.code_name === charter.code_name || match?.agent_id === charter.agent_id) {
      diagnostics.push({
        file: 'CHARTER.md',
        code: 'CHARTER.MULTI_AGENT.SELF_PEER',
        path,
        severity: 'error',
        message: `Agent "${charter.code_name}" cannot list itself as a peer`,
      });
      continue;
    }

    if (peer.cross_team_grant_id) {
      if (grants === undefined) continue;
      const grant = grants.find((g) => g.grant_id === peer.cross_team_grant_id);
      if (!grant) {
        diagnostics.push({
          file: 'CHARTER.md',
          code: 'CHARTER.MULTI_AGENT.GRANT_INVALID',
          path,
          severity: 'error',
          message: `cross_team_grant_id "${peer.cross_team_grant_id}" is not a known grant authorising this team to address peer "${peer.code_name}"`,
        });
        continue;
      }
      if (grant.revoked_at) {
        diagnostics.push({
          file: 'CHARTER.md',
          code: 'CHARTER.MULTI_AGENT.GRANT_INVALID',
          path,
          severity: 'error',
          message: `cross_team_grant_id "${peer.cross_team_grant_id}" was revoked at ${grant.revoked_at}`,
        });
        continue;
      }
      if (grant.expires_at && new Date(grant.expires_at) <= now) {
        diagnostics.push({
          file: 'CHARTER.md',
          code: 'CHARTER.MULTI_AGENT.GRANT_INVALID',
          path,
          severity: 'error',
          message: `cross_team_grant_id "${peer.cross_team_grant_id}" expired at ${grant.expires_at}`,
        });
        continue;
      }
      // Slack-specific: grant must authorise the bot_user_id the
      // charter declares. If the snapshot doesn't carry the slack
      // user_id (legacy callers), treat as null mismatch and surface
      // GRANT_INVALID — caller needs to update its grants fetcher.
      if ((grant.granted_agent_slack_user_id ?? null) !== peer.bot_user_id) {
        diagnostics.push({
          file: 'CHARTER.md',
          code: 'CHARTER.MULTI_AGENT.GRANT_INVALID',
          path,
          severity: 'error',
          message: `cross_team_grant_id "${peer.cross_team_grant_id}" authorises slack user_id ${grant.granted_agent_slack_user_id ?? 'null'}, but charter peer declares bot_user_id ${peer.bot_user_id}`,
        });
        continue;
      }
      if (grant.granted_to_agent_id && grant.granted_to_agent_id !== charter.agent_id) {
        diagnostics.push({
          file: 'CHARTER.md',
          code: 'CHARTER.MULTI_AGENT.GRANT_INVALID',
          path,
          severity: 'error',
          message: `cross_team_grant_id "${peer.cross_team_grant_id}" is scoped to agent_id ${grant.granted_to_agent_id}, but this charter is for agent_id ${charter.agent_id}`,
        });
        continue;
      }
      if (grant.capability_scope === 'grandfathered') {
        diagnostics.push({
          file: 'CHARTER.md',
          code: 'CHARTER.MULTI_AGENT.GRANT_GRANDFATHERED',
          path,
          severity: 'warning',
          message: `cross_team_grant_id "${peer.cross_team_grant_id}" is a Slack-backfill grandfathered grant for peer "${peer.code_name}". Confirm or revoke from team settings.`,
        });
      }
      continue;
    }

    if (!match) {
      diagnostics.push({
        file: 'CHARTER.md',
        code: 'CHARTER.MULTI_AGENT.UNKNOWN_PEER',
        path,
        severity: 'error',
        message: `No agent on this team has a Slack bot with bot_user_id ${peer.bot_user_id} (declared peer "${peer.code_name}")`,
      });
      continue;
    }

    if (match.code_name !== peer.code_name) {
      diagnostics.push({
        file: 'CHARTER.md',
        code: 'CHARTER.MULTI_AGENT.CODE_NAME_MISMATCH',
        path,
        severity: 'warning',
        message: `bot_user_id ${peer.bot_user_id} belongs to agent "${match.code_name}", but is listed under code_name "${peer.code_name}"`,
      });
    }

    const slackMode = match.slack_peer_agent_mode ?? null;
    if (slackMode === null || slackMode === 'off') {
      diagnostics.push({
        file: 'CHARTER.md',
        code: 'CHARTER.MULTI_AGENT.PEER_OPTED_OUT',
        path,
        severity: 'error',
        message: `Peer "${match.code_name}" has slack peer_agent_mode "${slackMode ?? 'unset'}"; set it to 'listen' or 'respond' on that agent's Slack channel config`,
      });
    }
  }
}
